import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import TableGridRow from './TableGridRow';
import AddIcon from '@mui/icons-material/Add';
import {
    StyledTableHead,
    StyledTableHeadAndRow,
    StyledTableHeadItem,
    StyledTableWrapper,
} from './TableGridFormik.styled';
import ScrollContainer from 'react-indiana-drag-scroll';
import Dependency from "../../../core/helpers/Dependency";
import { useAppDispatch } from "../../../commons/store/hooks";
import {
  fetchEcrfElements,
  fetchElementDataCollections,
  fetchElementDatas,
  resetElementDatas
} from "features/ecrf/redux/ecrfSlice";
import { postElementData } from "features/ecrf/redux/elementDataSlice";
import { useSnackbar } from "notistack";
import { RowHistoryModal } from "../RowHistoryModal";
import { useTranslation } from "react-i18next";
import Permission from 'core/helpers/Permission';
import { postElementDataCollection } from "features/ecrf/redux/elementDataCollectionSlice";
import { useParams } from "react-router-dom";

// =================================================================================
interface RowData {
  ecrf: string;
  element: string;
  data: string[];
}

export interface Props {
  name: string
  label?: string
  children?: any[]
  element: any;
  elementData: any;
  ecrfId: string
  options?: any;
  status?: boolean
  permissionOperations?: any
  isRefreshElement?: boolean
  setRefreshElement: (value: boolean) => void
}
// =================================================================================

const TableGridFormik = ({
  name,
  label,
  options,
  status,
  element,
  elementData,
  ecrfId,
  children,
  permissionOperations,
  isRefreshElement,
  setRefreshElement,
  ...props
}: Props) => {
    const dispatch = useAppDispatch()
    const { enqueueSnackbar} = useSnackbar();
    const { t } = useTranslation();
    const { id = ecrfId, section } = useParams<{ id: string, section: string }>();

    const [isDataLoading, setDataLoading]     = useState(false);
    const [isDataReloading, setDataReloading] = useState(false);
    const [isInitialLoading, setInitialLoading] = useState(false);

    /** State is holding actual dependencies */
    const [data, setData] = useState<any>({});
    const [dependencies, setDependencies] = useState<any>({});
    const [openHistory, setOpenHistory] = useState<any>(false);

    /**
     * This method is used to set value of reloading
     */
    const changeReloadingValue = async () => {
        setDataReloading(true);
        setDataReloading(false);
    }

    /**
     * This method is used to fetch element data and save it to state
     * @param id
     */
    const fetchElementData = async (id: string) => {
      if (!id) return;

      setInitialLoading(true);

      try {
        const elementData = await dispatch(fetchElementDatas(id)).unwrap();
        const collections = await dispatch(fetchElementDataCollections({ id: id })).unwrap()

        await handleDependencies(collections || []);
        setData(elementData || [])
      } catch (error: any) {
        enqueueSnackbar(error?.detail || error?.message, { variant: 'warning' });
      }

      setInitialLoading(false);
      return [];
    }

    const createRowData = async (): Promise<RowData[]> => {
      if (!children || (!id || !ecrfId)) return [];

      return children.map((element: { id: string }): RowData => ({
        ecrf: `/api/ecrves/${id || ecrfId}`,
        element: `/api/elements/${element.id}`,
        data: [''],
      }));
    };

    /**
     * Add new row to element data, if exists
     */
    const addNewRow = async () => {
      try {
        const data: any = await createRowData();
        const response = await dispatch(postElementDataCollection({
          datas: [`/api/element_datas/${elementData.id}`],
          elements: data
        })).unwrap();

        response?.id && await fetchElementData(elementData.id);
      } catch (error: any) {
        enqueueSnackbar(error?.detail || error?.message, { variant: 'warning' });
      }
    }

    /**
     * Put initial data and add first row
     */
    const addFirstRow = async () => {
      try {
        const data: any = await createRowData();

        const response = await dispatch(postElementData({
          ecrf: `/api/ecrves/${id || ecrfId}`,
          element: `/api/elements/${name}`,
          data: ['*'],
          collection: [{ elements: data }],
        })).unwrap();

        await dispatch(fetchEcrfElements({ id, sectionId: section }));
      } catch (error: any) {
        enqueueSnackbar(error?.detail || error?.message, { variant: 'warning' });
      }
    }

    /**
     * Handle new row
     */
    const handleAddRow = async () => {
      setDataLoading(true);

      // (data.collection && data.collection.length > 0) ? await addNewRow() : await addFirstRow();
      elementData?.id ? await addNewRow() : await addFirstRow();

      setDataLoading(false);
    }

    /**
     * Handle dependencies
     * @param collections
     */
    const handleDependencies = async (collections: any = []) => {
        if (!collections) {
            collections = data?.collection;
        }

        if (collections && collections.length > 0) {
            const dependencies = await Dependency.createDataCollection(element, collections);
            setDependencies(dependencies);
        } else {
            setDependencies([]);
        }
    }

    /**
     * Fetch history data of selected element
     * @param id
     */
    const handleHistory = async (id: string) => {
        setOpenHistory(id)
    };

    useEffect(() => {
        if (elementData?.id) {
            fetchElementData(elementData?.id);
        }

        /** clear state after unmount */
        return () => {
            dispatch(resetElementDatas());
        };
    }, [elementData])

    useEffect(() => {
        if (isRefreshElement) {
            if (elementData?.id) {
                setDependencies({})
                fetchElementData(elementData?.id).then();
                setRefreshElement(false);
            }
        }

        /** clear state after unmount */
        return () => {
            dispatch(resetElementDatas());
        };
    }, [isRefreshElement]);

    return (
        <>
            {/** children - columns names */}
            {children ? (
                <ScrollContainer ignoreElements={'input'}>
                    <StyledTableHeadAndRow>
                        <StyledTableWrapper>
                            <StyledTableHead size={children.length}>
                                {children?.map((element: any) => (
                                    <StyledTableHeadItem key={element.id}>{element?.title}</StyledTableHeadItem>
                                ))}
                            </StyledTableHead>

                            {dependencies?.length > 0 && !isDataReloading && (
                                <>
                                    {dependencies.map((row: any) => (
                                        <TableGridRow
                                            key={row.id}
                                            fetchData={fetchElementData.bind(this)}
                                            columns={children}
                                            row={row}
                                            dataId={data.id}
                                            changeReloadingValue={changeReloadingValue.bind(this)}
                                            handleHistory={handleHistory.bind(this)}
                                            status={status}
                                            options={element.options}
                                            permissionOperations={permissionOperations}
                                        />
                                    ))}
                                </>
                            )}

                            {(isInitialLoading || isDataLoading) && (
                                <Box display={'grid'} justifyContent={'center'} p={3}>
                                    <CircularProgress />
                                </Box>
                            )}

                            {element?.options?.addRow !== false && (
                                <Box width="100%" display={'grid'} justifyContent={'center'}>
                                    <Button
                                        startIcon={<AddIcon />}
                                        disabled={isDataLoading || (!Permission.hasAccess(permissionOperations, 'ADD') && status)}
                                        color={'primary'}
                                        size={'small'}
                                        style={{ marginTop: '30px' }}
                                        loading={isInitialLoading || isDataLoading || isDataReloading || undefined}
                                        loadingPosition="start"
                                        onClick={handleAddRow}>
                                        {t('add-a-row')}
                                    </Button>
                                </Box>
                            )}
                        </StyledTableWrapper>
                    </StyledTableHeadAndRow>

                    {/** Modal history */}
                    <RowHistoryModal children={children} open={openHistory} onClose={() => setOpenHistory(false)}/>
                </ScrollContainer>
            ) : (
                <Box display="block" width={'100%'} justifyContent={'center'}>
                    <CircularProgress />
                </Box>
            )}
        </>
    );
};

export default TableGridFormik;
